import React from 'react';
import { useTranslation } from 'react-i18next';

import WalletCashIcon from '@/assets/icons/wallet-cash';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar, ScreenNavBarProps } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { useActiveWallet } from '@/hooks/use-active-wallet';
import { useIsFirstScreenInStack } from '@/hooks/use-is-first-screen-in-stack';
import { LoadingScreen } from '@/screens/LoadingScreen';
import { toLocaleCurrency, toSocialCurrency } from '@/utils/numeric/currency';

type WithdrawalFrameProps = {
    children: React.ReactNode;
    showTitle?: boolean;
    closeIconMode?: ScreenNavBarProps['closeIconMode'];
    balance?: number;
    isLoading?: boolean;
    onClose?: () => void;
};

/**
 * Frame to contain withdraw views
 * */
export const WithdrawalFrame = ({
    closeIconMode,
    showTitle = true,
    balance = 0,
    isLoading = false,
    children,
    onClose,
}: WithdrawalFrameProps) => {
    const { t } = useTranslation(['transactions']);
    const { isSharedSocialWallet } = useActiveWallet();

    const isFirstScreenInStack = useIsFirstScreenInStack();

    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <Screen>
            <ScreenNavBar
                {...(showTitle
                    ? {
                          title: isSharedSocialWallet ? t('redeemable_betr_cash') : t('withdraw_funds'),
                          subTitle: (
                              <Row alignItems="center" justifyContent="center">
                                  <Text fontSize={13} color={'neutral300'}>
                                      {t('up_to')}
                                  </Text>
                                  {isSharedSocialWallet ? (
                                      <Box marginLeft="s4">
                                          <WalletCashIcon size={16} />
                                      </Box>
                                  ) : null}
                                  <Text fontSize={13} color={'neutral300'} marginLeft="s4">
                                      {isSharedSocialWallet ? toSocialCurrency(balance) : toLocaleCurrency(balance)}
                                  </Text>
                              </Row>
                          ),
                      }
                    : {})}
                closeIconMode={closeIconMode ?? (isFirstScreenInStack ? 'close' : 'back')}
                onClose={onClose ? onClose : undefined}
            />
            <>{children}</>
        </Screen>
    );
};
